// src/utils/activityMessages.jsx
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export function getActivityMessages(activities) {
  if (!activities?.length) return [];
  return activities.map((activity) => {
    const { action, metaData, user, updatedAt } = activity;
    const {
      caseNumber,
      candidate,
      assignedTo,
      company,
      reAssignedTo,
      check,
      section,
      order,
      currentStatus,
      content,
      subject,
      assignee,
    } = metaData || {};

    const userName = user?.firstName;
    const role = user?.role?.name;

    const dateObject = new Date(updatedAt);
    const formattedDate = dateObject.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    let message = '';
    switch (action) {
      case 'case_created':
        message = (
          <>
            Case {caseNumber} has been created for{' '}
            <b>{subject?.name || subject?.firstName || candidate?.firstName}</b>{' '}
            in {company?.name}.
          </>
        );
        break;
      case 'case_moved_to_entry':
        message = (
          <>
            Case <b>{caseNumber}</b> has been Moved To Entry.
          </>
        );
        break;
      case 'case_details_filled':
        message = (
          <>
            Details filled for Case <b>{caseNumber}</b>.
          </>
        );
        break;
      case 'task_assigned':
        message = (
          <>
            Case {caseNumber} : <b>{check?.name}</b> is assigned to{' '}
            {assignedTo?.firstName} {assignedTo?.lastName}.
          </>
        );
        break;
      case 'task_reassigned':
        message = (
          <>
            Case {caseNumber} : <b>{check?.name}</b> is re-assigned to{' '}
            {reAssignedTo?.firstName} {reAssignedTo?.lastName}.
          </>
        );
        break;
      case 'insufficiency_raised':
        message = (
          <>
            Case {caseNumber} : Insufficiency Raised For{' '}
            <b>
              {check?.name} ({section?.name} - {order})
            </b>
            .
          </>
        );
        break;
      case 'insufficiency_resolved':
        message = (
          <>
            Case {caseNumber} : Insufficiency Resolved For <b>{check?.name}</b>.
          </>
        );
        break;
      case 'task_submitted':
        message = (
          <>
            Case {caseNumber} : Task For <b>{check?.name}</b> has been
            submitted.
          </>
        );
        break;
      case 'case_assigned_to_qc':
        message = <>Case {caseNumber} Assigned to QC.</>;
        break;
      case 'case_status_changed':
        message = (
          <>
            Case {caseNumber} has been marked as <b>{currentStatus}</b>.
          </>
        );
        break;
      case 'comment_added':
        message = (
          <>
            Case {caseNumber} : Comment Added “{content}“.
          </>
        );
        break;
      case 'case_assigned':
        message = (
          <>
            Case {caseNumber} Assigned to{' '}
            <b>{`${assignee?.firstName ?? ''} ${assignee?.lastName ?? ''}`}</b>.
          </>
        );
        break;
      case 'case_check_assigned':
        message = (
          <>
            Check {check?.name} is assigned to ${assignee.firstName} $
            {assignee?.lastName || ''} for Case {caseNumber}.
          </>
        );
        break;
      case 'qc_completed':
        message = <> Qc Submitted for case {caseNumber} </>;
        break;
      case 'check_review_clear':
        message = (
          <>
            Review clear for {check?.name} in case {caseNumber}.
          </>
        );
        break;
      case 'check_status_changed':
        message = (
          <>
            Status of {check?.name} in {caseNumber} updated from{' '}
            {check?.previousStatus} to {check?.currentStatus}.
          </>
        );
        break;
      case 'case_checks_modified': {
        // Helper to render check names with tooltip for overflow
        const renderCheckNames = (checks, label) => {
          if (!checks?.length) return null;
          const firstTwo = checks
            .slice(0, 2)
            .map((val) => val?.checkName)
            .filter(Boolean);
          const rest = checks
            .slice(2)
            .map((val) => val?.checkName)
            .filter(Boolean);
          return (
            <>
              {firstTwo.join(', ')}
              {rest.length > 0 && (
                <span style={{ marginLeft: 4 }}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <span className="underline cursor-pointer text-blue-600">
                        +{rest.length} more
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-xs text-sm p-2">
                      <div className="whitespace-pre-line">
                        {rest.join(', ')}
                      </div>
                    </PopoverContent>
                  </Popover>
                </span>
              )}
              {label}
            </>
          );
        };
        const hasRemoved = check?.removedChecks?.length > 0;
        const hasAdded = check?.addedChecks?.length > 0;
        message = (
          <>
            {hasRemoved &&
              renderCheckNames(check?.removedChecks, ' has been removed')}
            {hasRemoved && hasAdded && ' and '}
            {hasAdded &&
              renderCheckNames(check?.addedChecks, ' has been added')}
            {(hasRemoved || hasAdded) && ` to case${caseNumber}`}
          </>
        );
        break;
      }
      case 'case_completed':
        message = (
          <>
            Case {caseNumber} has been successfully completed for{' '}
            <b>{subject?.name}</b>
          </>
        );
        break;
      case 'digital_address_verification_completed':
        message = (
          <>
            Digital address verification completed successfully for case{' '}
            <b>{caseNumber}</b>
          </>
        );
        break;
      default:
        message = <> </>;
    }
    return { action, message, userName, role, formattedDate };
  });
}
